<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';

const { t } = useI18n();
const isEnabled = ref(false);
const expirationDays = ref(60);
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const { wallet_enabled, wallet_expiration_days } =
      currentAccount.value?.settings || {};
    isEnabled.value = !!wallet_enabled;
    expirationDays.value = wallet_expiration_days || 60;
  },
  { deep: true, immediate: true }
);

const updateAccountSettings = async settings => {
  try {
    isSubmitting.value = true;
    await updateAccount(settings);
    useAlert(t('WALLET.settings.success'));
  } catch (error) {
    useAlert(t('WALLET.settings.error'));
  } finally {
    isSubmitting.value = false;
  }
};

const toggleWallet = async () => {
  return updateAccountSettings({
    wallet_enabled: isEnabled.value,
    wallet_expiration_days: expirationDays.value,
  });
};

const saveExpirationDays = async () => {
  return updateAccountSettings({
    wallet_enabled: isEnabled.value,
    wallet_expiration_days: expirationDays.value,
  });
};
</script>

<template>
  <SectionLayout
    v-if="currentAccount.feature_modules?.wallet !== false"
    :title="t('WALLET.settings.title')"
    :description="t('WALLET.settings.note')"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch v-model="isEnabled" @change="toggleWallet" />
      </div>
    </template>
    <form
      v-if="isEnabled"
      class="grid gap-4 max-w-sm"
      @submit.prevent="saveExpirationDays"
    >
      <WithLabel
        name="wallet-expiration-days"
        :label="t('WALLET.settings.expirationLabel')"
      >
        <NextInput
          v-model="expirationDays"
          type="number"
          min="1"
          class="w-full"
        />
      </WithLabel>
      <div>
        <NextButton blue :is-loading="isSubmitting" type="submit">
          {{ t('GENERAL_SETTINGS.SUBMIT') }}
        </NextButton>
      </div>
    </form>
  </SectionLayout>
</template>
